import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import {
  BarChart3, Calendar, BookOpen, Award, Users as UsersIcon, FlaskConical,
  MessageSquare, FileText, Building2, Flag, CreditCard, Star, Pencil,
  MessagesSquare, Newspaper, Presentation, Brain, Sparkles, FileBox,
  Handshake, ChevronDown, Mail, Image as ImageIcon, Briefcase, ScrollText, Bell, ListChecks, Headphones, Tag
} from 'lucide-react';
import { AdminAnalyticsTab } from '@/components/admin/AdminAnalyticsTab';
import { AdminNotificationsTab } from '@/components/admin/AdminNotificationsTab';
import { AdminRecommendationsTab } from '@/components/admin/AdminRecommendationsTab';
import { AdminEventsTab } from '@/components/admin/AdminEventsTab';
import { AdminCoursesTab } from '@/components/admin/AdminCoursesTab';
import { AdminSponsorsTab } from '@/components/admin/AdminSponsorsTab';
import { AdminUsersTab } from '@/components/admin/AdminUsersTab';
import AdminProfileOptionsTab from '@/components/admin/AdminProfileOptionsTab';
import { AdminBriteLabsTab } from '@/components/admin/AdminBriteLabsTab';
import { AdminFeedbackTab } from '@/components/admin/AdminFeedbackTab';
import { AdminResearchTab } from '@/components/admin/AdminResearchTab';
import { AdminCompaniesTab } from '@/components/admin/AdminCompaniesTab';
import { AdminReportsTab } from '@/components/admin/AdminReportsTab';
import { AdminSubscriptionsTab } from '@/components/admin/AdminSubscriptionsTab';
import { AdminAuditTab } from '@/components/admin/AdminAuditTab';
import { AdminForumTab } from '@/components/admin/AdminForumTab';
import { AdminPartnersTab } from '@/components/admin/AdminPartnersTab';
import { AdminNewsTab } from '@/components/admin/AdminNewsTab';
import { AdminKeynotesTab } from '@/components/admin/AdminKeynotesTab';
import AdminBriteCastTab from '@/components/admin/AdminBriteCastTab';
import AdminJobsTab from '@/components/admin/AdminJobsTab';
import { AdminKeliTab } from '@/components/admin/AdminKeliTab';
import { AdminWelcomeTab } from '@/components/admin/AdminWelcomeTab';
import { AdminDocumentationTab } from '@/components/admin/AdminDocumentationTab';
import { AdminCommsTab } from '@/components/admin/AdminCommsTab';
import { AdminPageExplainersTab } from '@/components/admin/AdminPageExplainersTab';
import { AdminBannersTab } from '@/components/admin/AdminBannersTab';
import { AdminPromoCodesTab } from '@/components/admin/AdminPromoCodesTab';
import { AdminBusinessTypesTab } from '@/components/admin/AdminBusinessTypesTab';
import { AdminGovernanceTab } from '@/components/admin/AdminGovernanceTab';

// Sectioned navigation — grouped by domain so the left rail stays scannable.
const SECTIONS = [
  {
    label: 'Insights',
    items: [
      { key: 'analytics', label: 'Analytics', icon: BarChart3 },
      { key: 'reports', label: 'Reports', icon: Flag },
      { key: 'audit', label: 'Edit Audit', icon: Pencil },
    ],
  },
  {
    label: 'Content & Programs',
    items: [
      { key: 'events', label: 'Events', icon: Calendar },
      { key: 'courses', label: 'Courses', icon: BookOpen },
      { key: 'keynotes', label: 'Keynotes', icon: Presentation },
      { key: 'britecast', label: 'BriteCast', icon: Headphones },
      { key: 'news', label: 'News', icon: Newspaper },
      { key: 'research', label: 'Learning', icon: FileText },
      { key: 'jobs', label: 'Jobs', icon: Briefcase },
      { key: 'documentation', label: 'Documentation', icon: FileBox },
    ],
  },
  {
    label: 'Community',
    items: [
      { key: 'users', label: 'Users', icon: UsersIcon },
      { key: 'profile_options', label: 'Profile Dropdowns', icon: ListChecks },
      { key: 'companies', label: 'Companies', icon: Building2 },
      { key: 'business_types', label: 'Business Types', icon: Briefcase },
      { key: 'partners', label: 'Partners', icon: Handshake },
      { key: 'sponsors', label: 'Sponsor Profiles', icon: Award },
      { key: 'banners', label: 'Banner Ads', icon: ImageIcon },
      { key: 'britelabs', label: 'BriteLabs', icon: FlaskConical },
      { key: 'forum', label: 'Forum', icon: MessagesSquare },
    ],
  },
  {
    label: 'Engagement',
    items: [
      { key: 'welcome', label: 'Welcome', icon: Sparkles },
      { key: 'page_explainers', label: 'Page Explainers', icon: BookOpen },
      { key: 'keli', label: 'Keli (AI)', icon: Brain },
      { key: 'recommendations', label: 'Recommendations', icon: Star },
      { key: 'feedback', label: 'Feedback', icon: MessageSquare },
    ],
  },
  {
    label: 'Operations',
    items: [
      { key: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
      { key: 'promo_codes', label: 'Promo Codes', icon: Tag },
      { key: 'comms', label: 'Comms', icon: Mail },
      { key: 'notifications', label: 'Notifications', icon: Bell },
      { key: 'governance', label: 'Governance', icon: ScrollText },
    ],
  },
];

const ALL_KEYS = SECTIONS.flatMap(s => s.items.map(i => i.key));

function AdminContent({ tab, user }) {
  switch (tab) {
    case 'analytics': return <AdminAnalyticsTab />;
    case 'events': return <AdminEventsTab />;
    case 'courses': return <AdminCoursesTab />;
    case 'news': return <AdminNewsTab />;
    case 'keynotes': return <AdminKeynotesTab />;
    case 'britecast': return <AdminBriteCastTab />;
    case 'jobs': return <AdminJobsTab />;
    case 'sponsors': return <AdminSponsorsTab />;
    case 'partners': return <AdminPartnersTab />;
    case 'users': return <AdminUsersTab currentUser={user} />;
    case 'profile_options': return <AdminProfileOptionsTab />;
    case 'britelabs': return <AdminBriteLabsTab />;
    case 'forum': return <AdminForumTab />;
    case 'feedback': return <AdminFeedbackTab />;
    case 'research': return <AdminResearchTab />;
    case 'companies': return <AdminCompaniesTab />;
    case 'business_types': return <AdminBusinessTypesTab />;
    case 'banners': return <AdminBannersTab />;
    case 'reports': return <AdminReportsTab />;
    case 'subscriptions': return <AdminSubscriptionsTab />;
    case 'promo_codes': return <AdminPromoCodesTab />;
    case 'audit': return <AdminAuditTab />;
    case 'recommendations': return <AdminRecommendationsTab />;
    case 'keli': return <AdminKeliTab />;
    case 'welcome': return <AdminWelcomeTab />;
    case 'page_explainers': return <AdminPageExplainersTab />;
    case 'documentation': return <AdminDocumentationTab />;
    case 'comms': return <AdminCommsTab />;
    case 'notifications': return <AdminNotificationsTab />;
    case 'governance': return <AdminGovernanceTab />;
    default: return <AdminAnalyticsTab />;
  }
}

export default function AdminPanel({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const initial = ALL_KEYS.includes(urlTab) ? urlTab : 'analytics';
  const [activeTab, setActiveTab] = useState(initial);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Keep URL in sync so admins can deep-link / share / bookmark a section
  useEffect(() => {
    if (searchParams.get('tab') !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const activeMeta = useMemo(() => {
    for (const sec of SECTIONS) {
      const hit = sec.items.find(i => i.key === activeTab);
      if (hit) return { section: sec.label, ...hit };
    }
    return { section: '', label: 'Admin', icon: BarChart3 };
  }, [activeTab]);

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation user={user} />
        <main className="pt-24 pb-12 px-6">
          <div className="container mx-auto text-center">
            <h1 className="text-3xl font-bold text-red-600 mb-4">Access Denied</h1>
            <p className="text-slate-600">Admin privileges required to access this page.</p>
          </div>
        </main>
      </div>
    );
  }

  const ActiveIcon = activeMeta.icon;

  const handleSelect = (key) => {
    setActiveTab(key);
    setMobileNavOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />

      <main className="pt-24 pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <div className="mb-6 md:mb-8">
            <h1 className="text-3xl md:text-4xl font-bold font-heading mb-1 md:mb-2 gradient-text">Admin Panel</h1>
            <p className="text-slate-600 text-sm md:text-base">Manage everything from one place</p>
          </div>

          {/* Mobile: collapsible section picker */}
          <div className="lg:hidden mb-4">
            <button
              type="button"
              onClick={() => setMobileNavOpen(v => !v)}
              className="w-full flex items-center justify-between bg-white border border-slate-200 rounded-lg px-4 py-3 shadow-sm"
              data-testid="admin-mobile-nav-toggle"
            >
              <span className="inline-flex items-center gap-2 font-semibold text-brand-navy">
                <ActiveIcon className="h-4 w-4" />
                {activeMeta.label}
              </span>
              <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${mobileNavOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileNavOpen && (
              <div className="mt-2 bg-white border border-slate-200 rounded-lg shadow-sm p-2 space-y-3 max-h-[60vh] overflow-y-auto">
                {SECTIONS.map(section => (
                  <div key={section.label}>
                    <p className="px-2 pt-1 pb-1.5 text-[10px] font-bold tracking-wider uppercase text-slate-400">{section.label}</p>
                    <div className="space-y-0.5">
                      {section.items.map(item => {
                        const Icon = item.icon;
                        const isActive = item.key === activeTab;
                        return (
                          <button
                            key={item.key}
                            type="button"
                            onClick={() => handleSelect(item.key)}
                            data-testid={`admin-${item.key}-tab`}
                            className={`w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm transition-colors ${
                              isActive
                                ? 'bg-brand-navy/10 text-brand-navy font-semibold'
                                : 'text-slate-600 hover:bg-slate-50'
                            }`}
                          >
                            <Icon className="h-4 w-4 flex-shrink-0" />
                            <span className="truncate">{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Desktop: 2-column layout */}
          <div className="grid grid-cols-12 gap-6">
            {/* Left rail */}
            <aside className="hidden lg:block lg:col-span-3 xl:col-span-2">
              <nav className="sticky top-24 bg-white border border-slate-200 rounded-xl p-3 shadow-sm max-h-[calc(100vh-7rem)] overflow-y-auto" data-testid="admin-sidebar">
                {SECTIONS.map((section, sIdx) => (
                  <div key={section.label} className={sIdx === 0 ? '' : 'mt-4'}>
                    <p className="px-2 pb-1.5 text-[10px] font-bold tracking-wider uppercase text-slate-400">
                      {section.label}
                    </p>
                    <div className="space-y-0.5">
                      {section.items.map(item => {
                        const Icon = item.icon;
                        const isActive = item.key === activeTab;
                        return (
                          <button
                            key={item.key}
                            type="button"
                            onClick={() => handleSelect(item.key)}
                            data-testid={`admin-${item.key}-tab`}
                            className={`group w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm transition-all ${
                              isActive
                                ? 'bg-gradient-to-r from-brand-navy/10 to-brand-sage/5 text-brand-navy font-semibold border-l-2 border-brand-navy -ml-0.5 pl-[9px]'
                                : 'text-slate-600 hover:bg-slate-50 hover:text-brand-navy'
                            }`}
                          >
                            <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-brand-navy' : 'text-slate-400 group-hover:text-brand-navy'}`} />
                            <span className="truncate">{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </nav>
            </aside>

            {/* Right content pane */}
            <section className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0" data-testid={`admin-${activeTab}-pane`}>
              <div className="bg-transparent">
                {/* Subtle section breadcrumb on desktop */}
                <div className="hidden lg:flex items-center gap-2 text-xs text-slate-400 mb-3">
                  <span>{activeMeta.section}</span>
                  <span>/</span>
                  <span className="text-brand-navy font-semibold">{activeMeta.label}</span>
                </div>
                <AdminContent tab={activeTab} user={user} />
              </div>
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
